import React, { useState } from 'react';
import {
  Card,
  Button,
  Select,
  DatePicker,
  Space,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Tabs,
  Divider,
  Radio,
  Checkbox,
  Empty,
} from 'antd';
import {
  BarChartOutlined,
  DownloadOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RiseOutlined,
  FallOutlined,
  SwapOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;

// 用户标签类型
type UserTag = 'all' | 'third-party-game' | 'live-game' | 'both-games' | 'deposit-user' | 'vip-user';

// 漏斗类型
type FunnelType = 'register' | 'deposit' | 'game' | 'withdraw' | 'activity';

// 漏斗数据接口
interface FunnelData {
  stage: string;
  count: number;
  rate: number;
  dropRate: number;
}

// 对比数据接口
interface CompareData {
  tag: string;
  data: FunnelData[];
  total: number;
}

const FunnelReport: React.FC = () => {
  const [funnelType, setFunnelType] = useState<FunnelType>('register');
  const [dateRange, setDateRange] = useState<any>([dayjs().subtract(7, 'day'), dayjs()]);
  const [selectedTags, setSelectedTags] = useState<UserTag[]>(['all']);
  const [compareMode, setCompareMode] = useState<boolean>(false);

  // 模拟漏斗数据 - 注册漏斗
  const registerFunnelData: Record<UserTag, FunnelData[]> = {
    all: [
      { stage: '访问用户', count: 10000, rate: 100, dropRate: 0 },
      { stage: '注册用户', count: 1500, rate: 15, dropRate: 85 },
      { stage: '活动奖励领取（视讯游戏券）', count: 1200, rate: 12, dropRate: 20 },
      { stage: '首次登录', count: 1100, rate: 11, dropRate: 8.3 },
      { stage: '使用视讯游戏券（进入视讯并投注）', count: 900, rate: 9, dropRate: 18.2 },
      { stage: '首次充值', count: 450, rate: 4.5, dropRate: 50 },
    ],
    'third-party-game': [
      { stage: '访问用户', count: 5000, rate: 100, dropRate: 0 },
      { stage: '注册用户', count: 800, rate: 16, dropRate: 84 },
      { stage: '活动奖励领取（视讯游戏券）', count: 650, rate: 13, dropRate: 18.8 },
      { stage: '首次登录', count: 600, rate: 12, dropRate: 7.7 },
      // 只玩三方游戏用户：使用视讯游戏券默认为0，用于体现跨场馆拉通失败
      { stage: '使用视讯游戏券（进入视讯并投注）', count: 0, rate: 0, dropRate: 100 },
      { stage: '首次充值', count: 300, rate: 6, dropRate: 45.5 },
    ],
    'live-game': [
      { stage: '访问用户', count: 3000, rate: 100, dropRate: 0 },
      { stage: '注册用户', count: 450, rate: 15, dropRate: 85 },
      { stage: '活动奖励领取（视讯游戏券）', count: 360, rate: 12, dropRate: 20 },
      { stage: '首次登录', count: 330, rate: 11, dropRate: 8.3 },
      { stage: '使用视讯游戏券（进入视讯并投注）', count: 250, rate: 8.3, dropRate: 24.2 },
      { stage: '首次充值', count: 100, rate: 3.3, dropRate: 60 },
    ],
    'both-games': [
      { stage: '访问用户', count: 2000, rate: 100, dropRate: 0 },
      { stage: '注册用户', count: 250, rate: 12.5, dropRate: 87.5 },
      { stage: '活动奖励领取（视讯游戏券）', count: 190, rate: 9.5, dropRate: 24 },
      { stage: '首次登录', count: 170, rate: 8.5, dropRate: 10.5 },
      { stage: '使用视讯游戏券（进入视讯并投注）', count: 100, rate: 5, dropRate: 41.2 },
      { stage: '首次充值', count: 50, rate: 2.5, dropRate: 50 },
    ],
    'deposit-user': [
      { stage: '访问用户', count: 8000, rate: 100, dropRate: 0 },
      { stage: '注册用户', count: 1200, rate: 15, dropRate: 85 },
      { stage: '活动奖励领取（视讯游戏券）', count: 1000, rate: 12.5, dropRate: 16.7 },
      { stage: '首次登录', count: 950, rate: 11.9, dropRate: 5 },
      { stage: '使用视讯游戏券（进入视讯并投注）', count: 800, rate: 10, dropRate: 15.8 },
      { stage: '首次充值', count: 450, rate: 5.6, dropRate: 43.8 },
    ],
    'vip-user': [
      { stage: '访问用户', count: 1500, rate: 100, dropRate: 0 },
      { stage: '注册用户', count: 300, rate: 20, dropRate: 80 },
      { stage: '活动奖励领取（视讯游戏券）', count: 280, rate: 18.7, dropRate: 6.7 },
      { stage: '首次登录', count: 270, rate: 18, dropRate: 3.6 },
      { stage: '使用视讯游戏券（进入视讯并投注）', count: 250, rate: 16.7, dropRate: 7.4 },
      { stage: '首次充值', count: 200, rate: 13.3, dropRate: 20 },
    ],
  };

  // 模拟漏斗数据 - 充值漏斗
  const depositFunnelData: Record<UserTag, FunnelData[]> = {
    all: [
      { stage: '注册用户', count: 1500, rate: 100, dropRate: 0 },
      { stage: '浏览充值页', count: 800, rate: 53.3, dropRate: 46.7 },
      { stage: '选择充值方式', count: 600, rate: 40, dropRate: 25 },
      { stage: '填写充值金额', count: 500, rate: 33.3, dropRate: 16.7 },
      { stage: '确认充值', count: 480, rate: 32, dropRate: 4 },
      { stage: '充值成功', count: 450, rate: 30, dropRate: 6.3 },
    ],
    'third-party-game': [
      { stage: '注册用户', count: 800, rate: 100, dropRate: 0 },
      { stage: '浏览充值页', count: 500, rate: 62.5, dropRate: 37.5 },
      { stage: '选择充值方式', count: 400, rate: 50, dropRate: 20 },
      { stage: '填写充值金额', count: 350, rate: 43.8, dropRate: 12.5 },
      { stage: '确认充值', count: 330, rate: 41.3, dropRate: 5.7 },
      { stage: '充值成功', count: 300, rate: 37.5, dropRate: 9.1 },
    ],
    'live-game': [
      { stage: '注册用户', count: 450, rate: 100, dropRate: 0 },
      { stage: '浏览充值页', count: 200, rate: 44.4, dropRate: 55.6 },
      { stage: '选择充值方式', count: 150, rate: 33.3, dropRate: 25 },
      { stage: '填写充值金额', count: 120, rate: 26.7, dropRate: 20 },
      { stage: '确认充值', count: 110, rate: 24.4, dropRate: 8.3 },
      { stage: '充值成功', count: 100, rate: 22.2, dropRate: 9.1 },
    ],
    'both-games': [
      { stage: '注册用户', count: 250, rate: 100, dropRate: 0 },
      { stage: '浏览充值页', count: 150, rate: 60, dropRate: 40 },
      { stage: '选择充值方式', count: 100, rate: 40, dropRate: 33.3 },
      { stage: '填写充值金额', count: 80, rate: 32, dropRate: 20 },
      { stage: '确认充值', count: 60, rate: 24, dropRate: 25 },
      { stage: '充值成功', count: 50, rate: 20, dropRate: 16.7 },
    ],
    'deposit-user': [
      { stage: '注册用户', count: 1200, rate: 100, dropRate: 0 },
      { stage: '浏览充值页', count: 700, rate: 58.3, dropRate: 41.7 },
      { stage: '选择充值方式', count: 550, rate: 45.8, dropRate: 21.4 },
      { stage: '填写充值金额', count: 480, rate: 40, dropRate: 12.7 },
      { stage: '确认充值', count: 460, rate: 38.3, dropRate: 4.2 },
      { stage: '充值成功', count: 450, rate: 37.5, dropRate: 2.2 },
    ],
    'vip-user': [
      { stage: '注册用户', count: 300, rate: 100, dropRate: 0 },
      { stage: '浏览充值页', count: 250, rate: 83.3, dropRate: 16.7 },
      { stage: '选择充值方式', count: 230, rate: 76.7, dropRate: 8 },
      { stage: '填写充值金额', count: 220, rate: 73.3, dropRate: 4.3 },
      { stage: '确认充值', count: 210, rate: 70, dropRate: 4.5 },
      { stage: '充值成功', count: 200, rate: 66.7, dropRate: 4.8 },
    ],
  };

  // 模拟漏斗数据 - 游戏漏斗
  const gameFunnelData: Record<UserTag, FunnelData[]> = {
    all: [
      { stage: '访问游戏页', count: 5000, rate: 100, dropRate: 0 },
      { stage: '选择游戏', count: 3000, rate: 60, dropRate: 40 },
      { stage: '进入游戏', count: 2000, rate: 40, dropRate: 33.3 },
      { stage: '首次下注', count: 1200, rate: 24, dropRate: 40 },
      { stage: '持续游戏', count: 800, rate: 16, dropRate: 33.3 },
      { stage: '复购游戏', count: 500, rate: 10, dropRate: 37.5 },
    ],
    'third-party-game': [
      { stage: '访问游戏页', count: 3000, rate: 100, dropRate: 0 },
      { stage: '选择游戏', count: 2000, rate: 66.7, dropRate: 33.3 },
      { stage: '进入游戏', count: 1500, rate: 50, dropRate: 25 },
      { stage: '首次下注', count: 1000, rate: 33.3, dropRate: 33.3 },
      { stage: '持续游戏', count: 700, rate: 23.3, dropRate: 30 },
      { stage: '复购游戏', count: 500, rate: 16.7, dropRate: 28.6 },
    ],
    'live-game': [
      { stage: '访问游戏页', count: 1500, rate: 100, dropRate: 0 },
      { stage: '选择游戏', count: 800, rate: 53.3, dropRate: 46.7 },
      { stage: '进入游戏', count: 400, rate: 26.7, dropRate: 50 },
      { stage: '首次下注', count: 150, rate: 10, dropRate: 62.5 },
      { stage: '持续游戏', count: 80, rate: 5.3, dropRate: 46.7 },
      { stage: '复购游戏', count: 30, rate: 2, dropRate: 62.5 },
    ],
    'both-games': [
      { stage: '访问游戏页', count: 500, rate: 100, dropRate: 0 },
      { stage: '选择游戏', count: 200, rate: 40, dropRate: 60 },
      { stage: '进入游戏', count: 100, rate: 20, dropRate: 50 },
      { stage: '首次下注', count: 50, rate: 10, dropRate: 50 },
      { stage: '持续游戏', count: 20, rate: 4, dropRate: 60 },
      { stage: '复购游戏', count: 10, rate: 2, dropRate: 50 },
    ],
    'deposit-user': [
      { stage: '访问游戏页', count: 4000, rate: 100, dropRate: 0 },
      { stage: '选择游戏', count: 2500, rate: 62.5, dropRate: 37.5 },
      { stage: '进入游戏', count: 1800, rate: 45, dropRate: 28 },
      { stage: '首次下注', count: 1200, rate: 30, dropRate: 33.3 },
      { stage: '持续游戏', count: 900, rate: 22.5, dropRate: 25 },
      { stage: '复购游戏', count: 600, rate: 15, dropRate: 33.3 },
    ],
    'vip-user': [
      { stage: '访问游戏页', count: 800, rate: 100, dropRate: 0 },
      { stage: '选择游戏', count: 700, rate: 87.5, dropRate: 12.5 },
      { stage: '进入游戏', count: 650, rate: 81.3, dropRate: 7.1 },
      { stage: '首次下注', count: 600, rate: 75, dropRate: 7.7 },
      { stage: '持续游戏', count: 550, rate: 68.8, dropRate: 8.3 },
      { stage: '复购游戏', count: 500, rate: 62.5, dropRate: 9.1 },
    ],
  };

  // 获取当前漏斗数据
  const getCurrentFunnelData = (): Record<UserTag, FunnelData[]> => {
    switch (funnelType) {
      case 'register':
        return registerFunnelData;
      case 'deposit':
        return depositFunnelData;
      case 'game':
        return gameFunnelData;
      default:
        return registerFunnelData;
    }
  };

  // 获取漏斗标题
  const getFunnelTitle = (): string => {
    switch (funnelType) {
      case 'register':
        return '注册转化漏斗';
      case 'deposit':
        return '充值转化漏斗';
      case 'game':
        return '游戏转化漏斗';
      case 'withdraw':
        return '提现转化漏斗';
      case 'activity':
        return '活动参与漏斗';
      default:
        return '注册转化漏斗';
    }
  };

  // 获取标签显示名称
  const getTagName = (tag: UserTag): string => {
    const tagMap: Record<UserTag, string> = {
      all: '全部用户',
      'third-party-game': '只玩三方游戏',
      'live-game': '直播游戏用户',
      'both-games': '双游戏用户',
      'deposit-user': '充值用户',
      'vip-user': 'VIP用户',
    };
    return tagMap[tag] || tag;
  };

  // 获取标签颜色
  const getTagColor = (tag: UserTag): string => {
    const colorMap: Record<UserTag, string> = {
      all: 'blue',
      'third-party-game': 'green',
      'live-game': 'orange',
      'both-games': 'purple',
      'deposit-user': 'cyan',
      'vip-user': 'gold',
    };
    return colorMap[tag] || 'default';
  };

  // 渲染漏斗图
  const renderFunnelChart = (data: FunnelData[], tag: UserTag) => {
    const maxCount = Math.max(...data.map(d => d.count));
    
    return (
      <div style={{ marginTop: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <Tag color={getTagColor(tag)} style={{ fontSize: 14, padding: '4px 12px' }}>
            {getTagName(tag)}
          </Tag>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.map((item, index) => {
            const width = (item.count / maxCount) * 100;
            return (
              <div key={index} style={{ position: 'relative' }}>
                <div
                  style={{
                    width: `${width}%`,
                    height: 50,
                    background: `linear-gradient(90deg, #1890ff ${width}%, #91d5ff 100%)`,
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    color: 'white',
                    fontWeight: 'bold',
                    transition: 'all 0.3s',
                    minWidth: '200px',
                  }}
                >
                  <span>{item.stage}</span>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <span>{item.count.toLocaleString()}</span>
                    <span style={{ fontSize: 12, opacity: 0.9 }}>
                      {item.rate.toFixed(1)}%
                    </span>
                  </div>
                </div>
                {index < data.length - 1 && (
                  <div
                    style={{
                      textAlign: 'center',
                      color: '#ff4d4f',
                      fontSize: 12,
                      marginTop: 4,
                      marginBottom: 4,
                    }}
                  >
                    流失 {item.dropRate.toFixed(1)}% ({((item.count - data[index + 1].count) / item.count * 100).toFixed(1)}%)
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // 渲染对比分析
  const renderCompareAnalysis = () => {
    const funnelData = getCurrentFunnelData();
    const selectedData = selectedTags.map(tag => ({
      tag,
      data: funnelData[tag],
      total: funnelData[tag][0]?.count || 0,
    }));

    return (
      <Row gutter={[16, 16]}>
        {selectedData.map((item, index) => (
          <Col xs={24} lg={selectedData.length === 1 ? 24 : 12} key={index}>
            <Card>
              {renderFunnelChart(item.data, item.tag)}
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  // 导出报表
  const handleExport = () => {
    const funnelData = getCurrentFunnelData();
    const exportData = {
      funnelType: getFunnelTitle(),
      dateRange: dateRange?.map((d: any) => d.format('YYYY-MM-DD')),
      tags: selectedTags,
      data: selectedTags.map(tag => ({
        tag: getTagName(tag),
        data: funnelData[tag],
      })),
    };

    const element = document.createElement('a');
    const file = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    element.download = `漏斗报表_${getFunnelTitle()}_${dayjs().format('YYYY-MM-DD')}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // 表格列定义
  const getTableColumns = () => {
    return [
      {
        title: '阶段',
        dataIndex: 'stage',
        key: 'stage',
        width: 150,
        fixed: 'left' as const,
      },
      {
        title: '人数',
        dataIndex: 'count',
        key: 'count',
        width: 120,
        render: (count: number) => count.toLocaleString(),
      },
      {
        title: '未使用人数（相对上一阶段）',
        dataIndex: 'unused',
        key: 'unused',
        width: 140,
        render: (unused: number | undefined, record: any, index: number) =>
          index === 0 || unused == null ? '-' : unused.toLocaleString(),
      },
      {
        title: '转化率',
        dataIndex: 'rate',
        key: 'rate',
        width: 120,
        render: (rate: number) => (
          <Progress percent={rate} size="small" format={() => `${rate.toFixed(1)}%`} />
        ),
      },
      {
        title: '流失率',
        dataIndex: 'dropRate',
        key: 'dropRate',
        width: 120,
        render: (dropRate: number) => (
          <Tag color={dropRate > 50 ? 'red' : dropRate > 30 ? 'orange' : 'green'}>
            {dropRate.toFixed(1)}%
          </Tag>
        ),
      },
    ];
  };

  const funnelData = getCurrentFunnelData();
  const currentData = selectedTags.length > 0 ? funnelData[selectedTags[0]] : [];

  return (
    <div>
      <div className="page-header">
        <h1>漏斗模型报表</h1>
        <p>分析用户行为转化路径，识别流失环节，优化转化效果</p>
      </div>

      {/* 筛选条件 */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>漏斗类型：</span>
              <Select
                value={funnelType}
                onChange={setFunnelType}
                style={{ width: '100%' }}
              >
                <Option value="register">注册转化漏斗</Option>
                <Option value="deposit">充值转化漏斗</Option>
                <Option value="game">游戏转化漏斗</Option>
                <Option value="withdraw">提现转化漏斗</Option>
                <Option value="activity">活动参与漏斗</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>时间范围：</span>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>用户标签：</span>
              <Select
                mode="multiple"
                value={selectedTags}
                onChange={setSelectedTags}
                style={{ width: '100%' }}
                placeholder="选择用户标签"
              >
                <Option value="all">全部用户</Option>
                <Option value="third-party-game">只玩三方游戏</Option>
                <Option value="live-game">直播游戏用户</Option>
                <Option value="both-games">双游戏用户</Option>
                <Option value="deposit-user">充值用户</Option>
                <Option value="vip-user">VIP用户</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>分析模式：</span>
              <Radio.Group
                value={compareMode}
                onChange={(e) => setCompareMode(e.target.value)}
                style={{ width: '100%' }}
              >
                <Radio.Button value={false}>单漏斗分析</Radio.Button>
                <Radio.Button value={true}>对比分析</Radio.Button>
              </Radio.Group>
            </Space>
          </Col>
        </Row>
        <Divider />
        <Space>
          <Button type="primary" icon={<BarChartOutlined />}>
            查询
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            导出报表
          </Button>
        </Space>
      </Card>

      {/* 漏斗分析 */}
      <Card title={getFunnelTitle()} extra={
        <Space>
          <Tag color="blue">总访问: {currentData[0]?.count.toLocaleString() || 0}</Tag>
          <Tag color="green">最终转化: {currentData[currentData.length - 1]?.count.toLocaleString() || 0}</Tag>
          <Tag color="orange">
            总转化率: {currentData.length > 0 
              ? ((currentData[currentData.length - 1].count / currentData[0].count) * 100).toFixed(2)
              : 0}%
          </Tag>
        </Space>
      }>
        {compareMode ? (
          renderCompareAnalysis()
        ) : (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={16}>
              {currentData.length > 0 ? (
                renderFunnelChart(currentData, selectedTags[0] || 'all')
              ) : (
                <Empty description="请选择用户标签" />
              )}
            </Col>
            <Col xs={24} lg={8}>
              <Card title="数据明细" size="small">
                <Table
                  columns={getTableColumns()}
                  dataSource={currentData.map((item, index) => {
                    const prev = index > 0 ? currentData[index - 1] : undefined;
                    const unused =
                      prev && typeof prev.count === 'number'
                        ? Math.max(prev.count - item.count, 0)
                        : undefined;
                    return { ...item, key: index, unused };
                  })}
                  pagination={false}
                  size="small"
                  scroll={{ x: 400 }}
                />
              </Card>
              <Card title="关键指标" size="small" style={{ marginTop: 16 }}>
                <Row gutter={[8, 8]}>
                  <Col span={12}>
                    <Statistic
                      title="首阶段人数"
                      value={currentData[0]?.count || 0}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="末阶段人数"
                      value={currentData[currentData.length - 1]?.count || 0}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="总转化率"
                      value={currentData.length > 0 
                        ? ((currentData[currentData.length - 1].count / currentData[0].count) * 100).toFixed(2)
                        : 0}
                      suffix="%"
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="平均流失率"
                      value={currentData.length > 1
                        ? (currentData.slice(0, -1).reduce((sum, item) => sum + item.dropRate, 0) / (currentData.length - 1)).toFixed(1)
                        : 0}
                      suffix="%"
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        )}
      </Card>

      {/* 对比分析详情 */}
      {compareMode && selectedTags.length > 1 && (
        <Card title="对比分析详情" style={{ marginTop: 24 }}>
          <Table
            columns={[
              {
                title: '阶段',
                dataIndex: 'stage',
                key: 'stage',
                fixed: 'left' as const,
                width: 150,
              },
              ...selectedTags.map(tag => ({
                title: getTagName(tag),
                key: tag,
                children: [
                  {
                    title: '人数',
                    dataIndex: `${tag}_count`,
                    key: `${tag}_count`,
                    width: 100,
                    render: (count: number) => count?.toLocaleString() || '-',
                  },
                  {
                    title: '未使用人数',
                    dataIndex: `${tag}_unused`,
                    key: `${tag}_unused`,
                    width: 110,
                    render: (unused: number | undefined, record: any, rowIndex: number) =>
                      rowIndex === 0 || unused == null ? '-' : unused.toLocaleString(),
                  },
                  {
                    title: '转化率',
                    dataIndex: `${tag}_rate`,
                    key: `${tag}_rate`,
                    width: 100,
                    render: (rate: number) => rate ? `${rate.toFixed(1)}%` : '-',
                  },
                ],
              })),
            ]}
            dataSource={funnelData[selectedTags[0]]?.map((item, index) => {
              const row: any = {
                key: index,
                stage: item.stage,
              };
              selectedTags.forEach(tag => {
                const tagData = funnelData[tag][index];
                const prevData = index > 0 ? funnelData[tag][index - 1] : undefined;
                const unused =
                  prevData && typeof prevData.count === 'number' && typeof (tagData?.count) === 'number'
                    ? Math.max(prevData.count - (tagData?.count || 0), 0)
                    : undefined;
                row[`${tag}_count`] = tagData?.count;
                row[`${tag}_unused`] = unused;
                row[`${tag}_rate`] = tagData?.rate;
              });
              return row;
            }) || []}
            pagination={false}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}
    </div>
  );
};

export default FunnelReport;

